import type { TFunction } from "i18next";
import type { DefinitionTooltipInfo } from "src/components/common";
import type { OverviewAttributionSource } from "../utils/paidMediaOverviewAttributionContext";

/**
 * Definition-tooltip copy lives in the locale files under
 * `paidMediaOverview.tooltips.*`; this module only resolves it.
 *
 * An entry is either flat, or split into `platform` / `shopify` children when
 * the wording itself changes with the attribution mode. Entries that merely
 * append the mode's data-basis sentence stay flat and reference it through
 * i18next nesting: `$t(paidMediaOverview.tooltips.dataBasis.{{mode}})`.
 */
const TOOLTIP_BASE = "paidMediaOverview.tooltips";

const readString = (
  t: TFunction,
  key: string,
  mode: OverviewAttributionSource
): string | undefined => {
  const value = t(key, { mode, defaultValue: "" });
  return typeof value === "string" && value.trim() ? value : undefined;
};

const readBullets = (
  t: TFunction,
  key: string,
  mode: OverviewAttributionSource
): string[] | undefined => {
  const value = t(key, { mode, returnObjects: true, defaultValue: [] }) as unknown;
  if (!Array.isArray(value)) {
    return undefined;
  }
  const bullets = value.filter((item): item is string => typeof item === "string" && Boolean(item));
  return bullets.length ? bullets : undefined;
};

const readTooltip = (
  t: TFunction,
  base: string,
  mode: OverviewAttributionSource
): DefinitionTooltipInfo | null => {
  const description = readString(t, `${base}.description`, mode);
  const formula = readString(t, `${base}.formula`, mode);
  const bullets = readBullets(t, `${base}.bullets`, mode);

  if (!description && !formula && !bullets) {
    return null;
  }

  return {
    description: description ?? "",
    formula,
    bullets,
    exampleTxt: readString(t, `${base}.exampleTxt`, mode),
    note: readString(t, `${base}.note`, mode),
    dataBasis: readString(t, `${base}.dataBasis`, mode),
  };
};

/** The "Data basis: …" sentence for the active attribution mode. */
export function overviewDataBasis(t: TFunction, mode: OverviewAttributionSource): string {
  return t(`${TOOLTIP_BASE}.dataBasis.${mode}`);
}

export function getOverviewTooltip(
  t: TFunction,
  id: string,
  mode: OverviewAttributionSource
): DefinitionTooltipInfo {
  const base = `${TOOLTIP_BASE}.${id}`;
  return (
    readTooltip(t, `${base}.${mode}`, mode) ??
    readTooltip(t, base, mode) ?? { description: id }
  );
}
